import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Sparkles, ArrowRight, Loader2, Wand2, ShieldCheck, CreditCard, Info } from 'lucide-react';
import { toast } from 'sonner';
import { TrustBadge } from './TrustBadge';
import { MarketplaceDisclaimer } from './MarketplaceDisclaimer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = {
  in_home_care: 'In-home care',
  telehealth_visit: 'Telehealth visit',
  wellness_visit: 'Wellness visit',
  companion_care: 'Companion care',
  post_discharge: 'Post-discharge',
  chronic_care: 'Chronic care',
  hospice_palliative: 'Hospice & palliative',
  mobile_urgent_care: 'Mobile urgent care',
  behavioral_health: 'Behavioral health',
  skilled_nursing: 'Skilled nursing',
};

const URGENCY_LABELS = {
  routine: 'Routine (this week)',
  soon: 'Soon (next 48h)',
  same_day: 'Same day',
  urgent: 'Urgent (within hours)',
};

/**
 * NegotiateModal — patient sends initial offer to a clinician.
 * Includes AI fair-price suggestion.
 */
export const NegotiateModal = ({ open, onOpenChange, clinician, token, onCreated }) => {
  const [serviceType, setServiceType] = useState('in_home_care');
  const [urgency, setUrgency] = useState('routine');
  const [duration, setDuration] = useState(2);
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');
  const [suggestion, setSuggestion] = useState(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [patientInsurance, setPatientInsurance] = useState(null);
  const [useInsurance, setUseInsurance] = useState(false);

  useEffect(() => {
    if (open) {
      setServiceType('in_home_care');
      setUrgency('routine');
      setDuration(2);
      setRate('');
      setNotes('');
      setSuggestion(null);
      if (token) {
        axios.get(`${API}/patient/profile`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => {
            const p = r.data || {};
            if (p.insurance_payer_id) {
              setPatientInsurance({ payer_id: p.insurance_payer_id, provider: p.insurance_provider, member_id: p.insurance_id });
              setUseInsurance(true);
            } else {
              setPatientInsurance(null);
              setUseInsurance(false);
            }
          })
          .catch(() => {});
      }
    }
  }, [open, token]);

  const fetchSuggestion = async () => {
    setLoadingAi(true);
    try {
      const { data } = await axios.post(
        `${API}/ai/fair-price`,
        { service_type: serviceType, urgency, duration_hours: Number(duration) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuggestion(data);
      if (!rate) setRate(String(data.suggested_rate));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI suggestion unavailable');
    } finally {
      setLoadingAi(false);
    }
  };

  const submit = async () => {
    const proposed = Number(rate);
    if (!proposed || proposed <= 0) {
      toast.error('Enter a valid hourly rate');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/offers`,
        {
          clinician_id: clinician.id,
          service_type: serviceType,
          proposed_rate: proposed,
          duration_hours: Number(duration),
          urgency,
          notes: notes || null,
          use_insurance: useInsurance && !!patientInsurance,
          insurance_payer_id: patientInsurance?.payer_id || null,
          insurance_member_id: patientInsurance?.member_id || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Offer sent to clinician');
      onCreated && onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send offer');
    } finally {
      setSubmitting(false);
    }
  };

  const total = rate && duration ? (Number(rate) * Number(duration)).toFixed(2) : '0.00';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="negotiate-modal">
        <div className="p-6">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-cn-text">
              Send an offer
            </DialogTitle>
            <DialogDescription className="text-cn-text-muted">
              Negotiate directly with{' '}
              <span className="font-semibold text-cn-text">
                {clinician?.first_name || clinician?.name || 'this clinician'}
              </span>
              . Funds are secured up-front and only settle after the visit is verified.
            </DialogDescription>
          </DialogHeader>

          {/* How negotiating works — clarity for first-time users */}
          <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-cn-sage-100/70 dark:bg-cn-ink-800 border border-cn-border p-3.5" data-testid="negotiate-how-it-works">
            <div className="w-8 h-8 rounded-full bg-cn-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Info className="w-4 h-4 text-cn-primary" />
            </div>
            <div>
              <div className="font-semibold text-sm text-cn-text">How negotiating works</div>
              <p className="text-xs text-cn-text-muted leading-relaxed mt-0.5">
                You propose the <span className="font-semibold text-cn-text">rate, duration, and visit details</span> below.{' '}
                {clinician?.first_name || clinician?.name || 'The clinician'} can accept your offer or send a counter — you&apos;ll be
                notified and can adjust. <span className="font-semibold text-cn-text">Nothing is charged</span> until you both agree;
                funds are then secured and only settle after the visit is verified.
              </p>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Service</Label>
                <Select value={serviceType} onValueChange={setServiceType}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-xl border-cn-border bg-white" data-testid="negotiate-service-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(SERVICE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Urgency</Label>
                <Select value={urgency} onValueChange={setUrgency}>
                  <SelectTrigger className="mt-1.5 h-11 rounded-xl border-cn-border bg-white" data-testid="negotiate-urgency-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(URGENCY_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Duration (hrs)</Label>
                <Input
                  type="number" min={0.5} max={24} step={0.5}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="mt-1.5 h-11 rounded-xl border-cn-border bg-white"
                  data-testid="negotiate-duration-input"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Your offer ($/hr)</Label>
                <Input
                  type="number" min={1} max={10000} step={1}
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder="e.g. 75"
                  className="mt-1.5 h-11 rounded-xl border-cn-border bg-white"
                  data-testid="negotiate-rate-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold text-cn-text-muted uppercase tracking-wide">Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Anything the clinician should know about the visit..."
                className="mt-1.5 h-11 rounded-xl border-cn-border bg-white"
                data-testid="negotiate-notes-input"
              />
            </div>

            {/* Insurance context */}
            {patientInsurance ? (
              <label className="flex items-center justify-between gap-3 rounded-2xl border border-cn-border bg-cn-bone-100 p-3.5 cursor-pointer" data-testid="negotiate-insurance-row">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-cn-primary/10 flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-4 h-4 text-cn-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-cn-text">Use my insurance</div>
                    <div className="text-xs text-cn-text-muted">{patientInsurance.provider}{patientInsurance.member_id ? ` · ${patientInsurance.member_id}` : ''}</div>
                  </div>
                </div>
                <Checkbox checked={useInsurance} onCheckedChange={setUseInsurance} data-testid="negotiate-use-insurance" />
              </label>
            ) : (
              <div className="text-xs text-cn-text-muted flex items-center gap-2 rounded-2xl border border-dashed border-cn-border p-3" data-testid="negotiate-no-insurance">
                <CreditCard className="w-3.5 h-3.5 flex-shrink-0" /> Add your insurance on your dashboard to attach coverage to this offer.
              </div>
            )}

            {/* AI fair price helper */}
            <div className="rounded-2xl border border-cn-border bg-cn-bone-100 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-cn-terracotta-50 border border-cn-terracotta-200 flex items-center justify-center flex-shrink-0">
                    <Wand2 className="w-4 h-4 text-cn-terracotta-500" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-cn-text">AI fair-price suggestion</div>
                    <div className="text-xs text-cn-text-muted">Get a transparent market range before you offer.</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={fetchSuggestion}
                  disabled={loadingAi}
                  className="cn-btn cn-btn-ghost text-xs !py-2 !px-3 flex-shrink-0"
                  data-testid="negotiate-ai-suggest-button"
                >
                  {loadingAi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {loadingAi ? 'Thinking…' : 'Suggest'}
                </button>
              </div>
              {suggestion && (
                <div className="mt-3 space-y-2" data-testid="negotiate-ai-result">
                  <div className="flex items-baseline gap-3">
                    <span className="font-display text-2xl font-bold text-cn-primary">${suggestion.suggested_rate}/hr</span>
                    <span className="text-xs text-cn-text-muted">range ${suggestion.rate_range.low}–${suggestion.rate_range.high}</span>
                  </div>
                  {suggestion.ai_note && (
                    <p className="text-xs text-cn-text leading-relaxed italic">&ldquo;{suggestion.ai_note}&rdquo;</p>
                  )}
                  <div className="flex gap-2">
                    <TrustBadge variant="ai-confidence" score={suggestion.confidence} />
                  </div>
                </div>
              )}
            </div>

            {/* Total preview */}
            <div className="flex items-center justify-between rounded-2xl bg-cn-primary text-white px-4 py-3">
              <div className="text-xs uppercase tracking-wider text-white/70">Visit total</div>
              <div className="font-display text-xl font-bold" data-testid="negotiate-total">${total}</div>
            </div>

            <div className="flex items-start gap-2 text-xs text-cn-text-muted">
              <ShieldCheck className="w-4 h-4 text-cn-primary flex-shrink-0 mt-0.5" />
              <span>Funds are secured up-front via Stripe Connect and settle to the clinician only after the visit is verified.</span>
            </div>

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="cn-btn cn-btn-ghost flex-1"
                data-testid="negotiate-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="cn-btn cn-btn-primary flex-1"
                data-testid="negotiate-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send offer <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>

            <MarketplaceDisclaimer variant="micro" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NegotiateModal;
